import React, { useContext, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Card,
  CardHeader,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import {
  AcUnit as AcUnitIcon,
  DataUsage as DataUsageIcon,
  Quiz as QuizIcon,
  ScatterPlot as ScatterPlotIcon,
  Timeline as TimelineIcon,
  ViewColumn as ViewColumnIcon,
  ViewHeadline as ViewHeadlineIcon,
} from "@mui/icons-material";
import { fetchData } from "../utils/api";
import { UserContext } from "../App";
import Plotting from "./Plotting";
import RangeSelector from "./UIComponents/RangeSelector";

export default function Inspector() {
  const theme = useTheme();
  // init internationalization
  const { t } = useTranslation();

  // get data from current dataset
  const {
    datasets,
    currentDatasetIdx,
    experiments,
    currentExperimentIdx,
    currentExperimentProgress,
  } = useContext(UserContext);
  const [experimentStats, setExperimentStats] = useState({
    targetNames: [],
    infoPlotsData: {},
  });
  const [timestamps, setTimestamps] = useState([]);
  const [targetSeries, setTargetSeries] = useState([]);
  const [range, setRange] = useState({ start: 0, end: 1000 });
  const [currentTarget, setCurrentTarget] = useState("");
  const [useChunking, setUseChunking] = useState(true); // default to chunking for large datasets
  const [isTargetLoading, setIsTargetLoading] = useState(false);

  const fetchTargetRange = async (nextRange = null) => {
    if (!currentTarget) {
      return;
    }
    setIsTargetLoading(true);
    const activeRange = nextRange || range;
    if (!useChunking) {
      console.log("Using full data from infoPlotsData (≤1000 points)");
      setTimestamps(experimentStats.timeSeriesPlotsIdx);
      setTargetSeries(experimentStats.infoPlotsData[currentTarget]);
      setIsTargetLoading(false);
      return;
    }
    const datasetName = datasets[currentDatasetIdx]?.datasetName;
    const experimentName = experiments[currentExperimentIdx]?.experimentName;

    const url = `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasetName}/experiment/${experimentName}/target-data?start=${activeRange.start}&end=${activeRange.end}`;

    try {
      const data = await fetchData(url);

      if (
        !data.timestamps?.length ||
        !data.target_data?.[currentTarget]?.length
      ) {
        // No data or empty slice returned
        setTimestamps([]);
        setTargetSeries([]);
        console.warn("No data for given range or empty response");
        return;
      }

      setTimestamps(data.timestamps);
      setTargetSeries(data.target_data[currentTarget]);
    } catch (error) {
      console.error("Failed to fetch target data:", error);
      setTimestamps([]);
      setTargetSeries([]);
    } finally {
      setIsTargetLoading(false);
    }
  };
  const fetchExperimentStats = () => {
    fetchData(
      `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasets[currentDatasetIdx].datasetName}/experiment/${experiments[currentExperimentIdx].experimentName}`
    ).then((data) => {
      // set experiment stats
      setExperimentStats(data);
      setCurrentTarget(data.targetNames[0]);
      setUseChunking(data.numDataPoints > 1000);

      if (data.targetNames.length > 0) setCurrentTarget(data.targetNames[0]);
      // create Year-Season-Value-Mapping
      // Get unique years
      const uniqueYears = [...new Set(data.seasonsPlotsData.year)];

      // Initialize an array to store the result
      const result = [];

      // Iterate over unique years
      for (let i = 0; i < uniqueYears.length; i++) {
        const year = uniqueYears[i];
        // Initialize arrays to store the mean values and seasons for the current year
        const meanValues = [];
        const seasons = [];

        // Filter the data for the current year
        const filteredData = data.seasonsPlotsData.Wh.filter(
          (_, index) => data.seasonsPlotsData.year[index] === year
        );
        const filteredSeasons = data.seasonsPlotsData.season.filter(
          (_, index) => data.seasonsPlotsData.year[index] === year
        );

        // Iterate over unique seasons
        // eslint-disable-next-line no-restricted-syntax
        for (const season of [...new Set(filteredSeasons)]) {
          // Filter the data for the current season within the current year
          const filteredSeasonData = filteredData.filter(
            (_, index) => filteredSeasons[index] === season
          );

          // Calculate the mean value for the current season within the current year
          const meanValue =
            filteredSeasonData.reduce((sum, value) => sum + value, 0) /
            filteredSeasonData.length;

          // Push the mean value and season to their respective arrays
          meanValues.push(meanValue);
          seasons.push(season);
        }

        // Create the dictionary object for the current year
        const yearData = {
          x: seasons,
          y: meanValues,
          name: year,
          type: "bar",
        };
        // Push the yearData object to the result array
        result.push(yearData);
      }
    });
  };
  const handleTargetChange = (event) => {
    setCurrentTarget(event.target.value);
  };
  const handleReset = () => {
    const defaultRange = { start: 0, end: 1000 };
    setRange(defaultRange);
    fetchTargetRange(defaultRange);
  };

  // set Experiment Data by API requests, when the current dataset + experiment is gathered
  useEffect(() => {
    if (datasets.length >= 1) {
      fetchExperimentStats();
      fetchTargetRange();
    }
  }, [experiments, currentExperimentIdx]);
  useEffect(() => {
    if (!currentTarget) {
      return;
    }
    fetchTargetRange();
  }, [currentTarget, useChunking]);

  return (
    <Grid id="inspector-container" container>
      <Grid
        container
        sx={{
          padding: theme.spacing(8, 8),
          borderRadius: "16px",
          backgroundColor: "white",
        }}
      >
        <Grid item xs={12}>
          <Typography variant="h4" gutterBottom>
            {t("inspect.PageTitle")}
          </Typography>
        </Grid>
        {
          // only render if current experiment has fulfilled all required steps and stats are loaded
          // eslint-disable-next-line no-nested-ternary
          currentExperimentProgress?.created && experimentStats ? (
            <>
              <Grid item xs={12} sx={{ margin: theme.spacing(4, 0) }}>
                <Grid container spacing={3}>
                  {/*
                                        Data Information Cards
                                    */}
                  <Grid container spacing={2}>
                    <Grid item xl={3} lg={4} sm={6} xs={12}>
                      <Card
                        // className="inspect-info-card"
                        sx={{
                          borderRadius: 3,
                          boxShadow: 3,
                          "&:hover": { boxShadow: 6 },
                        }}
                      >
                        <CardHeader
                          avatar={
                            <Avatar
                            // sx={{ bgcolor: "primary.main" }}
                            >
                              <ViewHeadlineIcon />
                            </Avatar>
                          }
                          title={
                            <Typography variant="h5" color="text.secondary">
                              {t("inspect.NumRows")}
                            </Typography>
                          }
                          subheader={
                            <Typography variant="h5">
                              {experimentStats.numDataPoints}
                            </Typography>
                          }
                        />
                      </Card>
                    </Grid>

                    <Grid item xl={3} lg={4} sm={6} xs={12}>
                      <Card
                        // className="inspect-info-card"
                        sx={{
                          borderRadius: 3,
                          boxShadow: 3,
                          "&:hover": { boxShadow: 6 },
                        }}
                      >
                        <CardHeader
                          avatar={
                            <Avatar
                            // sx={{ bgcolor: "secondary.main" }}
                            >
                              <ViewColumnIcon />
                            </Avatar>
                          }
                          title={
                            <Typography variant="h5" color="text.secondary">
                              {t("inspect.NumFeatures")}
                            </Typography>
                          }
                          subheader={
                            <Typography variant="h5">
                              {experimentStats.featureDim}
                            </Typography>
                          }
                        />
                      </Card>
                    </Grid>

                    <Grid item xl={3} lg={4} sm={6} xs={12}>
                      <Card
                        // className="inspect-info-card"
                        sx={{
                          borderRadius: 3,
                          boxShadow: 3,
                          "&:hover": { boxShadow: 6 },
                        }}
                      >
                        <CardHeader
                          avatar={
                            <Avatar
                            // sx={{ bgcolor: "success.main" }}
                            >
                              <ScatterPlotIcon />
                            </Avatar>
                          }
                          title={
                            <Typography variant="h5" color="text.secondary">
                              {t("inspect.NumTargets")}
                            </Typography>
                          }
                          subheader={
                            <Typography variant="h5">
                              {experimentStats.targetDim}
                            </Typography>
                          }
                        />
                      </Card>
                    </Grid>

                    <Grid item xl={3} lg={4} sm={6} xs={12}>
                      <Card
                        // className="inspect-info-card"
                        sx={{
                          borderRadius: 3,
                          boxShadow: 3,
                          "&:hover": { boxShadow: 6 },
                        }}
                      >
                        <CardHeader
                          avatar={
                            <Avatar
                            // sx={{ bgcolor: "info.main" }}
                            >
                              <DataUsageIcon />
                            </Avatar>
                          }
                          title={
                            <Typography variant="h5" color="text.secondary">
                              {t("inspect.ValRatio")}
                            </Typography>
                          }
                          subheader={
                            <Box display="flex" alignItems="center">
                              <Typography variant="h5" sx={{ mr: 1 }}>
                                {`${experimentStats.trainValRation * 100}% / ${
                                  100 - experimentStats.trainValRation * 100
                                }%`}
                              </Typography>
                              <Tooltip
                                title={t("inspect.TrainValSplitHint")}
                                arrow
                              >
                                <IconButton size="small" sx={{ padding: 0.5 }}>
                                  <InfoIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          }
                        />
                      </Card>
                    </Grid>
                  </Grid>

                  {experimentStats.hasTestst ? (
                    <Grid item xl={3} lg={4} xs={6}>
                      <Card className="inspect-info-card">
                        <CardHeader
                          avatar={
                            <Avatar
                              sx={{
                                bgcolor: theme.palette.success.light,
                                color: theme.palette.success.dark,
                              }}
                            >
                              <QuizIcon />
                            </Avatar>
                          }
                          title={t("inspect.UseTestset")}
                          subheader=""
                        />
                        <span className="explanation">
                          {t("inspect.ExplanationTestset")}
                        </span>
                      </Card>
                    </Grid>
                  ) : (
                    ""
                  )}
                  {experimentStats.isTimeseries ? (
                    <Grid item xl={3} lg={4} xs={6}>
                      <Card className="inspect-info-card">
                        <CardHeader
                          avatar={
                            <Avatar
                              sx={{
                                bgcolor: theme.palette.success.light,
                                color: theme.palette.success.dark,
                              }}
                            >
                              <TimelineIcon />
                            </Avatar>
                          }
                          title={t("inspect.IsTimeseries")}
                          subheader=""
                        />
                        <div>
                          <span className="explanation">
                            {t("inspect.ExplanationTimeseries")}
                          </span>
                        </div>
                      </Card>
                    </Grid>
                  ) : (
                    ""
                  )}
                  {experimentStats.isSeasonal ? (
                    <Grid item xl={3} lg={4} xs={6}>
                      <Card className="inspect-info-card">
                        <CardHeader
                          avatar={
                            <Avatar
                              sx={{
                                bgcolor: theme.palette.success.light,
                                color: theme.palette.success.dark,
                              }}
                            >
                              <AcUnitIcon />
                            </Avatar>
                          }
                          title={t("inspect.IsSeasonal")}
                        />
                        <span className="explanation">
                          {t("inspect.ExplanationSeasonal")}
                        </span>
                      </Card>
                    </Grid>
                  ) : (
                    ""
                  )}
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Grid container columnSpacing={8}>
                  {/*
                        Target Plots
                    */}
                  <Grid item xs={12} sx={{ marginTop: theme.spacing(6) }}>
                    <Typography variant="h4">
                      {t("inspect.TargetPlots")}
                    </Typography>
                    <br />
                  </Grid>
                  {experimentStats.targetNames.length > 1 && (
                    <Grid item xs={12} sx={{ marginBottom: theme.spacing(4) }}>
                      <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>{t("inspect.TargetDropdown")}</InputLabel>
                        <Select
                          value={currentTarget}
                          onChange={handleTargetChange}
                          label={t("inspect.TargetDropdown")}
                        >
                          {experimentStats.targetNames.map((name) => (
                            <MenuItem key={name} value={name}>
                              {name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Tooltip title={t("inspect.HintTarget")}>
                        <IconButton>
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  )}
                  {experimentStats.isTimeseries &&
                  experimentStats.infoPlotsData[currentTarget] ? (
                    <Grid item lg={6} xs={11}>
                      {useChunking && (
                        <RangeSelector
                          range={range}
                          setRange={setRange}
                          onLoad={() => fetchTargetRange()}
                          onReset={handleReset}
                        />
                      )}
                      {isTargetLoading ? (
                        <Box
                          sx={{
                            minHeight: 240,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <CircularProgress />
                        </Box>
                      ) : (
                        <Plotting
                          runData={experimentStats}
                          type="scatterTime"
                          currentTarget={currentTarget}
                          timestamps={timestamps}
                          targetSeries={targetSeries}
                        />
                      )}
                    </Grid>
                  ) : (
                    ""
                  )}
                  <Grid item lg={6} xs={11}>
                    {experimentStats.infoPlotsData[currentTarget] ? (
                      <Plotting
                        key={`${currentTarget}-hist`}
                        runData={experimentStats}
                        type="histogram"
                        currentTarget={currentTarget}
                      />
                    ) : (
                      ""
                    )}
                  </Grid>
                  {/* {experimentStats.isTimeseries ? */}
                  {/*    <> */}
                  {/*        <Grid item xs={6} sx={{"marginTop": theme.spacing(2)}}> */}
                  {/*            <Plot */}
                  {/*                data={seasonData} */}
                  {/*                layout={{ */}
                  {/*                    title: t('inspect.SeasonalDistribution'), */}
                  {/*                    yaxis: { */}
                  {/*                        title: `Durchschnitts ${currentTarget}` */}
                  {/*                    }, */}
                  {/*                    showlegend: true, */}
                  {/*                    barmode: 'group', */}
                  {/*                    bargap: 0.15, */}
                  {/*                    bargroupgap: 0.1, */}
                  {/*                    margin: { */}
                  {/*                        l: 80, */}
                  {/*                        r: 40, */}
                  {/*                        b: 80, */}
                  {/*                        t: 100, */}
                  {/*                    }, */}
                  {/*                }} */}
                  {/*            /> */}
                  {/*        </Grid> */}
                  {/*    </> */}
                  {/*    : "" */}
                  {/* } */}
                  {/* <Grid item xs={2} sx={{marginTop: theme.spacing(6)}}> */}
                  {/*    <Badge color="error" badgeContent={13}> */}
                  {/*        <Typography variant="h6">{t('inspect.WarningPlots')}</Typography> */}
                  {/*    </Badge> */}
                  {/* </Grid> */}
                </Grid>
              </Grid>
            </>
          ) : currentExperimentProgress.processed ? (
            <Grid item xs={3} sx={{ margin: theme.spacing(6) }}>
              <CircularProgress />
            </Grid>
          ) : (
            // if user lands on this page, without having all required steps fulfilled, show hint
            <Typography variant="body">{t("hints.NotYetProcessed")}</Typography>
          )
        }
      </Grid>
    </Grid>
  );
}
